import { BrandKey, Unit, getBrandKeysForUnits, isUnit } from '@prismamedia/one-brandkey';
import { Role, isRole } from './types/Role.js';
import { RoleUnit, isRoleUnit } from './types/RoleUnit.js';
import { getEnumValues } from './utils/enum.js';

export * from './types/Role.js';
export * from './types/RoleUnit.js';

export interface RawUser {
  id: string;
  name: string | null;
  email: string | null;
  avatarUrl?: string | null;
  createdAt?: string;
  googleId?: string | null;
  roles: string[];
}

export type RoleMap = {
  [role in Role]?: Array<RoleUnit>;
};

export class InvalidUserError extends Error {}

export class User implements RawUser {
  readonly id: string;
  readonly name: string | null;
  readonly email: string;
  readonly avatarUrl: string | null;
  readonly createdAt: string;
  readonly googleId: string | null;
  readonly roles: Array<string>;
  readonly roleMap: RoleMap;

  constructor(user: RawUser) {
    if (!user.id || !user.email || !Array.isArray(user.roles)) {
      throw new InvalidUserError();
    }

    this.id = user.id;
    this.name = user.name;
    this.email = user.email;
    this.createdAt = user.createdAt || '';
    this.googleId = user.googleId || null;
    this.avatarUrl = user.avatarUrl || null;

    this.roles = user.roles.filter((roleWithUnit) => {
      const [role, brandKey] = roleWithUnit.split('-');

      return isRole(role) && (brandKey === 'ALL' || isUnit(brandKey));
    });

    this.roleMap = this.roles.reduce<RoleMap>((roleMap, role) => {
      const explodedRole = role.split('-');

      if (explodedRole.length === 2) {
        const [maybeRole, maybeUnit] = explodedRole;

        if (isRole(maybeRole) && isRoleUnit(maybeUnit)) {
          if (!(maybeRole in roleMap)) {
            roleMap[maybeRole] = [];
          }

          if (!roleMap[maybeRole]!.includes(maybeUnit)) {
            roleMap[maybeRole]?.push(maybeUnit);
          }
        }
      }

      return roleMap;
    }, {});
  }

  public hasRoleOnUnit(role: Role, unit: RoleUnit = 'ALL'): boolean {
    return (
      this.hasRole(role) &&
      (this.roleMap[role]!.includes(unit) ||
        (unit !== 'ALL' && this.roleMap[role]!.includes('ALL')))
    );
  }

  public hasRole(role: Role) {
    return isRole(role) && !!this.roleMap[role];
  }

  public hasRolesOnUnit(roles: Role[], unit?: RoleUnit): boolean {
    return roles.some((role) => this.hasRoleOnUnit(role, unit));
  }

  public hasRoleOnUnits(role: Role, units: Unit[]): boolean {
    return units.some((unit) => this.hasRoleOnUnit(role, unit));
  }

  public hasRolesOnUnits(roles: Role[], units: Unit[]): boolean {
    return roles.some((role) => units.some((unit) => this.hasRoleOnUnit(role, unit)));
  }

  public isSuperAdmin(): boolean {
    return this.hasRoleOnUnit(Role.Superadmin);
  }

  public assertIsSuperAdmin(): void {
    if (!this.isSuperAdmin()) {
      throw new Error(`User should be a superadmin`);
    }
  }

  public isAdmin(unit?: RoleUnit): boolean {
    return this.hasRoleOnUnit(Role.Admin, unit);
  }

  public assertIsAdmin(unit?: Unit): void {
    if (!this.isAdmin(unit)) {
      throw new Error(`User should be an admin`);
    }
  }

  public getUnitSet(excludedRoles: Array<Role> = []): Set<Unit> {
    const units = Object.entries(this.roleMap).reduce((currentUnits: Unit[], [role, units]) => {
      if (!excludedRoles.includes(role as Role) && units) {
        currentUnits.push(...(units.filter((unit) => unit !== 'ALL') as Unit[]));
      }

      return currentUnits as Unit[];
    }, []);

    return new Set<Unit>(units);
  }

  /**
   * "ALL" is a special unit
   */
  public getUnits(excludedRoles: Array<Role> = []): Unit[] {
    //We filter "ALL" from RoleUnit, so we can cast the return to Unit[]
    return Array.from(this.getUnitSet(excludedRoles)).filter((unit) => !['ALL'].includes(unit));
  }

  public getUnitsForRoles(roles: Array<Role>): Array<Unit> {
    let units: Set<Unit> = new Set();

    // using for so we can break early in case of a role with 'ALL'
    for (let i = 0; i < roles.length; i++) {
      if (this.hasRole(roles[i])) {
        const rolesUnits = this.roleMap[roles[i]];
        if (rolesUnits!.includes('ALL')) {
          return getEnumValues(Unit);
        }

        for (let unit of rolesUnits as Unit[]) {
          units.add(unit as Unit);
        }
      }
    }

    return Array.from(units);
  }

  public getUserBrandKeysForRoles(roles: Role[]): BrandKey[] {
    return getBrandKeysForUnits(this.getUnitsForRoles(roles));
  }
}
